import React, { useState } from "react";
import { Alert, Box, Snackbar, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toolsApi from "../../store/modules/tools/toolsApi";
import {
  TOOLS,
  TOOLS_QUERY_KEY,
  ToolName,
  isToolEnabled,
  useTools,
} from "../../hooks/useTools";
import LoadingDialog from "../Dialogs/LoadingDialog";
import SettingsToggleRow from "./SettingsToggleRow";

/** Settings > Tools: turn each tool on or off */
const ToolSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: tools, isError } = useTools();
  const [snackbar, setSnackbar] = useState<{
    message: string;
    severity: "success" | "error";
  } | null>(null);

  const isEnabled = (name: ToolName) => isToolEnabled(tools, name);

  const setTool = useMutation({
    mutationFn: ({ name, enabled }: { name: ToolName; enabled: boolean }) =>
      toolsApi.setTool(name, enabled),
    onSuccess: (_, { name, enabled }) => {
      const label = TOOLS.find((tool) => tool.name === name)!.label;
      setSnackbar({
        message: `${label} is ${enabled ? "on" : "off"}.`,
        severity: "success",
      });
      // A tool only shows or hides its menu item and route, so the switches are all that's stale
      queryClient.invalidateQueries({ queryKey: [TOOLS_QUERY_KEY] });
    },
    onError: () =>
      setSnackbar({
        message: "An error has occurred, please try again.",
        severity: "error",
      }),
  });

  const handleToggle = (name: ToolName) =>
    setTool.mutate({ name, enabled: !isEnabled(name) });

  return (
    <>
      <LoadingDialog open={setTool.isPending} />
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          boxShadow: "0 2px 8px 0 rgba(0,0,0,0.04)",
          p: { xs: 2, md: 4 },
          mb: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Turn tools on or off. A tool that is off is hidden from the menu.
        </Typography>
        {isError && (
          <Typography color="error" sx={{ mb: 2 }}>
            Your tool settings couldn't be loaded, please reload the page.
          </Typography>
        )}
        {TOOLS.map((tool) => (
          <SettingsToggleRow
            key={tool.name}
            label={tool.label}
            checked={isEnabled(tool.name)}
            disabled={!tools || setTool.isPending}
            onChange={() => handleToggle(tool.name)}
          />
        ))}
      </Box>

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={6000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar(null)}
          severity={snackbar?.severity ?? "success"}
          variant="filled"
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ToolSettings;
